// client/src/coms/accounts/accountsApi.js
// Saved accounts on this device (server: routes/accounts.js). The browser never holds anything that can sign in
// by itself: switching is a request the server honours only with the httpOnly device cookie and a live grant.
import { clearTabMemory } from '../workspace/tabMemory';

async function call(method, url, body) {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: { Accept: 'application/json', 'X-Vox-Request': '1', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data && (data.message || data.error)) || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data && data.error;
    throw err;
  }
  return data;
}

export const listAccounts = () => call('GET', '/api/accounts');
export const switchAccount = (userId) => call('POST', '/api/accounts/switch', { user_id: Number(userId) });
export const setDefaultAccount = (userId) => call('POST', '/api/accounts/default', { user_id: Number(userId) });
export const removeAccount = (userId) => call('DELETE', `/api/accounts/${encodeURIComponent(String(userId))}`);
export const signOutAllAccounts = () => call('POST', '/api/accounts/signout-all', {});
export const listSessions = () => call('GET', '/api/accounts/sessions');
export const revokeSession = (id) => call('DELETE', `/api/accounts/sessions/${encodeURIComponent(String(id))}`);

// ---------- one account per page ----------
// After the account changes, the whole app restarts so no state, stream, voice room or cache of the previous
// account survives (and every other open tab of this browser does the same).
const CHANNEL = 'vox_account';
const STORAGE_PING = 'vx:account-changed';

export function announceAccountChange(userId) {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(CHANNEL);
      ch.postMessage({ type: 'account-changed', userId: userId == null ? null : String(userId), at: Date.now() });
      ch.close();
    }
  } catch {}
  try {
    window.localStorage.setItem(STORAGE_PING, JSON.stringify({ userId: userId == null ? null : String(userId), at: Date.now() }));
  } catch {}
}

export function restartApp(path = '/comms') {
  clearTabMemory();
  try {
    window.location.assign(path);
  } catch {
    window.location.reload();
  }
}

// Calls onChange(newUserId) when another tab of this browser switched or signed out.
export function subscribeAccountChanges(onChange) {
  let ch = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => {
        if (e && e.data && e.data.type === 'account-changed') onChange(e.data.userId);
      };
    }
  } catch {}
  const onStorage = (e) => {
    if (!e || e.key !== STORAGE_PING || !e.newValue) return;
    try {
      onChange(JSON.parse(e.newValue).userId);
    } catch {}
  };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('storage', onStorage);
    try {
      if (ch) ch.close();
    } catch {}
  };
}

export async function fetchCurrentUserId() {
  try {
    const res = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
    const me = await res.json().catch(() => null);
    return me && me.id != null ? String(me.id) : null;
  } catch {
    return undefined; // unknown (network)
  }
}

// ---------- work that a switch would interrupt ----------
const activities = new Map();
let seq = 0;

export function beginActivity(kind, label) {
  seq += 1;
  const id = seq;
  activities.set(id, { kind, label });
  return () => activities.delete(id);
}

export function listInterruptions({ voiceSession, activeGame } = {}) {
  const out = [];
  const status = String((voiceSession && voiceSession.status) || '').toLowerCase();
  if (status === 'connected' || status === 'connecting' || status === 'joining') out.push('your voice call');
  if (activeGame) out.push('the activity you are in');
  for (const a of activities.values()) out.push(a.label || a.kind);
  return Array.from(new Set(out));
}
